import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CitySettingsModal from "components/Cities/CitySettingsModal/CitySettingsModal";
import {
    getCategoryOrder,
    resetCategoryOrder,
    saveCategoryOrder,
} from "services/vendorCategoryOrder";
import { moveSlider } from "utils/sliderOrder";
// The two move buttons are the ones of the sliders table.
import "components/Sliders/sliderOrder.scss";
import "components/Ui/AppPresenceBadge/appPresenceBadge.scss";
import "./categoryOrderModal.scss";

/**
 * «ترتيب الأقسام»: the order of the menu categories inside ONE restaurant.
 *
 * The categories are shared by every restaurant. This order belongs to the
 * restaurant alone, and the customer app shows the menu in it.
 *
 * The moves stay in the modal until «حفظ الترتيب». The shell is the city
 * settings modal: the same header, buttons and z-index above the sidebar.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.close
 * @param {number|string|null} props.vendorUserId - the restaurant's user id
 * @param {string} [props.vendorName]
 * @param {(type: string, head: string, content: string) => void} props.showMessage
 */
const CategoryOrderModal = ({ show, close, vendorUserId, vendorName, showMessage }) => {
    const queryClient = useQueryClient();
    const queryKey = ["vendorCategoryOrder", String(vendorUserId)];
    const { data, isFetching, isError } = useQuery({
        queryKey,
        queryFn: () => getCategoryOrder(vendorUserId),
        enabled: show && Boolean(vendorUserId),
        // Always the menu as it is now: a stale list is refused on save.
        staleTime: 0,
        gcTime: 0,
    });
    // The order after the moves in this modal. null: no move yet.
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    const categories = draft ?? data?.results ?? [];
    const hasNewCategory = Boolean(data?.custom) && categories.some((row) => !row.placed);

    const closeModal = () => {
        setDraft(null);
        close();
    };

    const move = (category, direction) => {
        const ids = categories.map((row) => row.id);
        const nextIds = moveSlider(ids, ids, category.id, direction);
        if (!nextIds) return;
        const byId = new Map(categories.map((row) => [row.id, row]));
        setDraft(nextIds.map((id) => byId.get(id)));
    };

    const run = async (action, successMessage) => {
        setSaving(true);
        try {
            await action();
            showMessage("success", "تم الحفظ", successMessage);
            closeModal();
        } catch (err) {
            showMessage(
                "error",
                "تعذر حفظ الترتيب",
                err.response?.data?.message || "حدث خطأ غير متوقع",
            );
            // The menu changed, or the save failed: show the real list again.
            setDraft(null);
            queryClient.invalidateQueries({ queryKey });
        } finally {
            setSaving(false);
        }
    };

    const submit = (event) => {
        event.preventDefault();
        if (!categories.length) return closeModal();
        return run(
            () =>
                saveCategoryOrder(
                    vendorUserId,
                    categories.map((row) => row.id),
                ),
            "تم حفظ ترتيب الأقسام",
        );
    };

    const reset = () =>
        run(() => resetCategoryOrder(vendorUserId), "عاد المطعم إلى الترتيب العام للأقسام");

    return (
        <CitySettingsModal
            show={show}
            close={closeModal}
            region={{ name: vendorName || "المطعم" }}
            title="ترتيب الأقسام"
            loading={saving || isFetching}
            onSubmit={submit}
            submitLabel="حفظ الترتيب"
        >
            <div className="city-settings-modal__section category-order">
                <p className="category-order__hint">
                    هذا ترتيب الأقسام في قائمة هذا المطعم كما يراها الزبون. الأول يظهر أولًا.
                    الترتيب خاص بهذا المطعم ولا يغيّر المطاعم الأخرى.
                </p>
                {hasNewCategory ? (
                    <div className="form-warning">
                        الأقسام المعلَّمة «جديد» أضيفت بعد آخر ترتيب، وهي في آخر القائمة.
                    </div>
                ) : null}
                {isError ? <div className="form-warning">تعذر تحميل أقسام المطعم</div> : null}
                {!isFetching && !isError && categories.length === 0 ? (
                    <div className="category-order__empty">لا توجد منتجات لهذا المطعم بعد.</div>
                ) : null}

                <ol className="category-order__list">
                    {categories.map((category, index) => (
                        <li key={category.id} className="category-order__row">
                            <span className="slider-order__place">{index + 1}</span>
                            <span className="category-order__name">
                                <bdi>{category.name}</bdi>
                                {data?.custom && !category.placed ? (
                                    <span className="app-presence app-presence--warn">جديد</span>
                                ) : null}
                            </span>
                            <span className="category-order__count">
                                <bdi dir="ltr">{category.totalProducts}</bdi> منتج
                            </span>
                            <span className="slider-order">
                                <button
                                    type="button"
                                    className="slider-order__move"
                                    disabled={index === 0}
                                    aria-label={`قدّم «${category.name}» مكانًا`}
                                    title="قدّم القسم"
                                    onClick={() => move(category, "up")}
                                >
                                    ↑
                                </button>
                                <button
                                    type="button"
                                    className="slider-order__move"
                                    disabled={index === categories.length - 1}
                                    aria-label={`أخّر «${category.name}» مكانًا`}
                                    title="أخّر القسم"
                                    onClick={() => move(category, "down")}
                                >
                                    ↓
                                </button>
                            </span>
                        </li>
                    ))}
                </ol>

                {data?.custom ? (
                    <button type="button" className="category-order__reset" onClick={reset}>
                        العودة إلى الترتيب العام
                    </button>
                ) : null}
            </div>
        </CitySettingsModal>
    );
};

export default CategoryOrderModal;
